import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, Search, Phone, Pencil, Trash2, Users, UserCheck, UserX, BarChart3, X, Pill, Printer, Link2, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { openPrintWindow } from '@/lib/letterhead';
import RiconciliazioneMMG from '@/components/RiconciliazioneMMG';
import CercaRecapitiDialog from '@/components/CercaRecapitiDialog';

const EMPTY_FORM = { cognome: '', nome: '', telefono: '', email: '', indirizzo_ambulatorio: '', codice_regionale: '' };

export default function MMG() {
  const navigate = useNavigate();
  const [medici, setMedici] = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [stats, setStats] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [farmaciDialog, setFarmaciDialog] = useState(null);
  const [farmaciData, setFarmaciData] = useState(null);
  const [loadingFarmaci, setLoadingFarmaci] = useState(false);
  const [showRiconciliazione, setShowRiconciliazione] = useState(false);
  const [recapitiPer, setRecapitiPer] = useState(null);

  const fetchMedici = useCallback(() => {
    api.get('/utility/mmg', { params: { q: search || '' } }).then(r => setMedici(r.data)).catch(() => {});
  }, [search]);

  const fetchStats = useCallback(() => {
    api.get('/utility/mmg/statistiche').then(r => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => { fetchMedici(); }, [fetchMedici]);
  useEffect(() => { if (showStats) fetchStats(); }, [showStats, fetchStats]);

  const handleSave = async () => {
    if (!form.cognome.trim() || !form.nome.trim()) { toast.error('Cognome e nome obbligatori'); return; }
    try {
      if (editId) {
        await api.put(`/utility/mmg/${editId}`, form);
        toast.success('Medico aggiornato');
      } else {
        await api.post('/utility/mmg', form);
        toast.success('Medico aggiunto');
      }
      setShowForm(false); setEditId(null); setForm(EMPTY_FORM);
      fetchMedici(); if (showStats) fetchStats();
    } catch { toast.error('Errore nel salvataggio'); }
  };

  const handleEdit = (m) => {
    setForm({ cognome: m.cognome, nome: m.nome, telefono: m.telefono || '', email: m.email || '', indirizzo_ambulatorio: m.indirizzo_ambulatorio || '', codice_regionale: m.codice_regionale || '' });
    setEditId(m.id); setShowForm(true);
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/utility/mmg/${id}`); toast.success('Medico eliminato'); setDeleteConfirm(null); fetchMedici(); if (showStats) fetchStats(); } catch { toast.error('Errore'); }
  };

  const openFarmaciList = async (medico) => {
    setFarmaciDialog(medico);
    setLoadingFarmaci(true);
    try {
      const res = await api.get(`/utility/mmg/${medico.id}/farmaci`);
      setFarmaciData(res.data);
    } catch { toast.error('Errore caricamento farmaci'); setFarmaciData(null); }
    setLoadingFarmaci(false);
  };

  const printFarmaciList = () => {
    if (!farmaciData || !farmaciDialog) return;
    let html = `<p><strong>Tel:</strong> ${farmaciDialog.telefono || 'N/A'} &nbsp;|&nbsp; <strong>Totale:</strong> ${farmaciData.totale_farmaci} farmaci</p>`;
    farmaciData.ospiti.forEach(o => {
      html += `<div class="section-title">${o.cognome} ${o.nome} ${o.codice_fiscale ? `<span style="font-size:9px;color:#64748b">(${o.codice_fiscale})</span>` : ''}</div>`;
      html += `<table><tr><th>Farmaco</th><th>Principio Attivo</th><th>Dosaggio</th><th>Via</th><th>Orari</th><th>Note</th></tr>`;
      o.terapie.forEach(t => {
        const orari = t.dosaggi_per_orario?.length > 1 && new Set(t.dosaggi_per_orario.map(d=>d.quantita)).size > 1
          ? t.dosaggi_per_orario.map(d => `${d.orario}: ${d.quantita}`).join(', ')
          : (t.orari || []).join(', ');
        html += `<tr><td>${t.farmaco}${t.al_bisogno ? ' <em>(al bisogno)</em>' : ''}</td><td>${t.principio_attivo || '-'}</td><td>${t.dosaggio}</td><td>${t.via || '-'}</td><td>${orari || '-'}</td><td></td></tr>`;
      });
      html += `</table>`;
    });
    openPrintWindow(`Lista Farmaci - Dott. ${farmaciDialog.cognome} ${farmaciDialog.nome}`, html, {
      subtitle: 'Elenco terapie in corso per medico di medicina generale'
    });
  };

  const sorted = [...medici].sort((a, b) => a.cognome.localeCompare(b.cognome));

  return (
    <div className="space-y-4" data-testid="mmg-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Medici di Medicina Generale</h1>
          <p className="text-xs text-slate-400">Gestione MMG e statistiche</p>
        </div>
        <div className="flex gap-2">
          <Button variant={showRiconciliazione ? 'default' : 'outline'} size="sm" onClick={() => setShowRiconciliazione(!showRiconciliazione)} data-testid="toggle-riconciliazione-btn">
            <Link2 className="w-4 h-4 mr-1" />Riconciliazione
          </Button>
          <Button variant={showStats ? 'default' : 'outline'} size="sm" onClick={() => setShowStats(!showStats)} data-testid="toggle-stats-btn">
            <BarChart3 className="w-4 h-4 mr-1" />{showStats ? 'Nascondi Report' : 'Report'}
          </Button>
          <Button size="sm" onClick={() => { setForm(EMPTY_FORM); setEditId(null); setShowForm(true); }} className="bg-brand hover:bg-brand-dark text-white" data-testid="add-mmg-btn">
            <Plus className="w-4 h-4 mr-1" />Nuovo Medico
          </Button>
        </div>
      </div>

      {/* Stats Panel */}
      {showStats && stats && (
        <div className="space-y-4" data-testid="mmg-stats-panel">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card><CardContent className="p-4 text-center">
              <Users className="w-6 h-6 mx-auto text-blue-500 mb-1" />
              <p className="text-2xl font-bold text-slate-800">{stats.totale_medici}</p>
              <p className="text-[10px] text-slate-400">Medici MMG</p>
            </CardContent></Card>
            <Card><CardContent className="p-4 text-center">
              <UserCheck className="w-6 h-6 mx-auto text-emerald-500 mb-1" />
              <p className="text-2xl font-bold text-slate-800">{stats.totale_ospiti}</p>
              <p className="text-[10px] text-slate-400">Ospiti Totali</p>
            </CardContent></Card>
            <Card><CardContent className="p-4 text-center">
              <UserX className="w-6 h-6 mx-auto text-red-500 mb-1" />
              <p className="text-2xl font-bold text-red-600">{stats.ospiti_senza_mmg}</p>
              <p className="text-[10px] text-slate-400">Senza MMG Assegnato</p>
            </CardContent></Card>
            <Card><CardContent className="p-4 text-center">
              <BarChart3 className="w-6 h-6 mx-auto text-amber-500 mb-1" />
              <p className="text-2xl font-bold text-slate-800">{stats.totale_ospiti > 0 && stats.totale_medici > 0 ? (stats.totale_ospiti / stats.totale_medici).toFixed(1) : '0'}</p>
              <p className="text-[10px] text-slate-400">Media Pz/Medico</p>
            </CardContent></Card>
          </div>

          {/* Distribution */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Distribuzione Pazienti per MMG</CardTitle></CardHeader>
            <CardContent className="space-y-1.5 max-h-[300px] overflow-y-auto">
              {stats.medici.filter(m => m.num_pazienti > 0).sort((a, b) => b.num_pazienti - a.num_pazienti).map(m => (
                <div key={m.id} className="flex items-center gap-3 text-xs">
                  <span className="w-36 font-medium truncate">{m.cognome} {m.nome}</span>
                  <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
                    <div className="bg-blue-500 h-full rounded-full transition-all" style={{ width: `${Math.min(100, (m.num_pazienti / Math.max(1, ...stats.medici.map(x => x.num_pazienti))) * 100)}%` }} />
                  </div>
                  <Badge className="bg-blue-100 text-blue-700 text-[10px]">{m.num_pazienti}</Badge>
                </div>
              ))}
              {stats.medici.filter(m => m.num_pazienti > 0).length === 0 && (
                <p className="text-xs text-slate-400 text-center py-3">Nessun ospite ha un MMG assegnato. Assegna i medici dalla scheda ospite.</p>
              )}
            </CardContent>
          </Card>

          {/* Guests without MMG */}
          {stats.ospiti_senza_mmg > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm text-red-600">Ospiti senza MMG ({stats.ospiti_senza_mmg})</CardTitle></CardHeader>
              <CardContent className="space-y-1 max-h-[200px] overflow-y-auto">
                {stats.ospiti_senza_mmg_list.map(o => (
                  <button key={o.id} onClick={() => navigate(`/ospiti/${o.id}`)} className="block w-full text-left text-xs text-brand hover:text-blue-800 hover:underline">
                    {o.cognome} {o.nome}
                  </button>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Search + Table */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca medico..." className="pl-9" data-testid="mmg-search" />
      </div>

      {showRiconciliazione && (
        <RiconciliazioneMMG onChanged={() => { fetchMedici(); if (showStats) fetchStats(); }} />
      )}

      <Table data-testid="mmg-table">
        <TableHeader>
          <TableRow><TableHead>Cognome</TableHead><TableHead>Nome</TableHead><TableHead>Telefono</TableHead><TableHead>Email</TableHead><TableHead>Codice Reg.</TableHead><TableHead></TableHead></TableRow>
        </TableHeader>
        <TableBody>
          {sorted.length === 0 ? (
            <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8 text-sm">Nessun medico trovato</TableCell></TableRow>
          ) : sorted.map(m => (
            <TableRow key={m.id} data-testid={`mmg-row-${m.id}`}>
              <TableCell className="font-semibold text-sm">{m.cognome}</TableCell>
              <TableCell className="text-sm">{m.nome}</TableCell>
              <TableCell className="text-sm">{m.telefono ? <a href={`tel:${m.telefono.replace(/\s/g, '')}`} className="flex items-center gap-1 text-brand hover:underline"><Phone className="w-3 h-3" />{m.telefono}</a> : <span className="text-slate-300">-</span>}</TableCell>
              <TableCell className="text-sm text-slate-500">{m.email || '-'}</TableCell>
              <TableCell className="text-sm text-slate-500">{m.codice_regionale || '-'}</TableCell>
              <TableCell>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openFarmaciList(m)} className="text-xs text-emerald-600 hover:text-emerald-700" data-testid={`mmg-farmaci-${m.id}`}><Pill className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => setRecapitiPer(m)} className="text-xs text-blue-600 hover:text-blue-700" title="Cerca recapiti sul web" data-testid={`mmg-recapiti-${m.id}`}><Globe className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(m)} className="text-xs"><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(m)} className="text-xs text-red-500"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {recapitiPer && (
        <CercaRecapitiDialog medico={recapitiPer} open={!!recapitiPer} onClose={() => setRecapitiPer(null)}
          onSaved={() => { fetchMedici(); if (showStats) fetchStats(); }} />
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={v => { if (!v) { setShowForm(false); setEditId(null); } }}>
        <DialogContent className="max-w-md" data-testid="mmg-form-dialog">
          <DialogHeader><DialogTitle>{editId ? 'Modifica Medico' : 'Nuovo Medico MMG'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Cognome *</Label><Input value={form.cognome} onChange={e => setForm(f => ({ ...f, cognome: e.target.value.toUpperCase() }))} data-testid="mmg-form-cognome" /></div>
              <div><Label className="text-xs">Nome *</Label><Input value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value.toUpperCase() }))} data-testid="mmg-form-nome" /></div>
            </div>
            <div><Label className="text-xs">Telefono</Label><Input value={form.telefono} onChange={e => setForm(f => ({ ...f, telefono: e.target.value }))} placeholder="Es. 320 1234567" data-testid="mmg-form-telefono" /></div>
            <div><Label className="text-xs">Email</Label><Input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} type="email" /></div>
            <div><Label className="text-xs">Indirizzo Ambulatorio</Label><Input value={form.indirizzo_ambulatorio} onChange={e => setForm(f => ({ ...f, indirizzo_ambulatorio: e.target.value }))} /></div>
            <div><Label className="text-xs">Codice Regionale</Label><Input value={form.codice_regionale} onChange={e => setForm(f => ({ ...f, codice_regionale: e.target.value }))} /></div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setShowForm(false); setEditId(null); }}>Annulla</Button>
              <Button onClick={handleSave} className="bg-brand hover:bg-brand-dark text-white" data-testid="mmg-form-save">{editId ? 'Aggiorna' : 'Aggiungi'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteConfirm} onOpenChange={v => { if (!v) setDeleteConfirm(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Elimina Medico</DialogTitle></DialogHeader>
          {deleteConfirm && (
            <div className="space-y-3">
              <p className="text-sm">Confermi l'eliminazione di <strong>{deleteConfirm.cognome} {deleteConfirm.nome}</strong>?</p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Annulla</Button>
                <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => handleDelete(deleteConfirm.id)}>Elimina</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Farmaci Dialog */}
      <Dialog open={!!farmaciDialog} onOpenChange={v => { if (!v) { setFarmaciDialog(null); setFarmaciData(null); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="farmaci-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pill className="w-5 h-5 text-emerald-600" />
              Lista Farmaci — {farmaciDialog?.cognome} {farmaciDialog?.nome}
            </DialogTitle>
          </DialogHeader>
          {loadingFarmaci ? (
            <div className="text-center py-8 text-slate-400 text-sm">Caricamento...</div>
          ) : farmaciData ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex gap-3 text-xs text-slate-500">
                  <span>Pazienti: <strong className="text-slate-700">{farmaciData.ospiti.length}</strong></span>
                  <span>Farmaci totali: <strong className="text-slate-700">{farmaciData.totale_farmaci}</strong></span>
                </div>
                <Button variant="outline" size="sm" onClick={printFarmaciList} data-testid="print-farmaci-btn">
                  <Printer className="w-4 h-4 mr-1" />Stampa
                </Button>
              </div>
              {farmaciData.ospiti.length === 0 ? (
                <p className="text-center py-8 text-sm text-slate-400">Nessun paziente con terapie attive per questo medico</p>
              ) : (
                farmaciData.ospiti.map(o => (
                  <div key={o.ospite_id} className="rounded-lg border border-slate-200 overflow-hidden">
                    <div className="px-3 py-2 bg-slate-50 flex items-center justify-between">
                      <button onClick={() => navigate(`/ospiti/${o.ospite_id}`)} className="font-bold text-sm text-blue-700 hover:underline">
                        {o.cognome} {o.nome}
                      </button>
                      {o.codice_fiscale && <span className="text-[10px] text-slate-400 font-mono">{o.codice_fiscale}</span>}
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-[10px]">Farmaco</TableHead>
                          <TableHead className="text-[10px]">P. Attivo</TableHead>
                          <TableHead className="text-[10px]">Dosaggio</TableHead>
                          <TableHead className="text-[10px]">Via</TableHead>
                          <TableHead className="text-[10px]">Orari</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {o.terapie.map((t, i) => (
                          <TableRow key={i} className={t.al_bisogno ? 'bg-amber-50/50' : ''}>
                            <TableCell className="text-xs font-medium">
                              {t.farmaco}
                              {t.al_bisogno && <Badge className="ml-1 text-[8px] bg-amber-100 text-amber-700">bisogno</Badge>}
                            </TableCell>
                            <TableCell className="text-[10px] text-slate-500">{t.principio_attivo || '-'}</TableCell>
                            <TableCell className="text-xs">
                              {t.dosaggi_per_orario?.length > 1 && new Set(t.dosaggi_per_orario.map(d => d.quantita)).size > 1
                                ? t.dosaggi_per_orario.map((d, j) => (
                                    <div key={j} className="flex items-center gap-1">
                                      <span className="text-[10px] font-mono text-brand bg-blue-50 px-1 rounded">{d.orario}</span>
                                      <span className="text-[10px]">{d.quantita}</span>
                                    </div>
                                  ))
                                : t.dosaggio
                              }
                            </TableCell>
                            <TableCell className="text-[10px] capitalize">{t.via || '-'}</TableCell>
                            <TableCell className="text-[10px] font-mono">{(t.orari || []).join(', ') || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ))
              )}
            </div>
          ) : (
            <p className="text-center py-8 text-sm text-red-400">Errore nel caricamento dei dati</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
